import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { prepacksApi } from '@/services/api'
import { normalizePrepack } from './prepackCardModel'
import { deleteConfirmMessage } from './prepackLibraryModel'

/**
 * The Library's delete (#1054; lifted for #1080): read the latest copies,
 * drop the ones the operator may not delete, confirm, delete one request per
 * pack, keep the failures selected. `selectedPacks` / `setSelectedPacks` are
 * the list's selection (a Map of packs): a bulk request belongs to the
 * selection it started from. `canDelete(pack)` mirrors the API;
 * `invalidate()` marks the lists stale; `setOpenPack` closes a drawer on a
 * deleted pack. `confirmProps` are the ConfirmDialog's, less the focus
 * fallback, which is the caller's region.
 */
export function usePackDelete({ slug, canDelete, selectedPacks, setSelectedPacks, invalidate, setOpenPack }) {
  // { packs, skipped }: the packs to delete and how many selected packs the
  // operator may not delete.
  const [pendingDelete, setPendingDelete] = useState(null)
  const [preparingDelete, setPreparingDelete] = useState(false)
  // The control that asked for a delete, kept before the read below
  // disables it (a disabled button loses focus); focus goes back there, or
  // to the caller's region when the delete took that control away (#1054).
  const deleteOpener = useRef(null)
  const slugRef = useRef(slug)
  useEffect(() => { slugRef.current = slug; setPendingDelete(null) }, [slug])
  const selectedPacksRef = useRef(selectedPacks)
  useEffect(() => { selectedPacksRef.current = selectedPacks }, [selectedPacks])

  // One request per pack; a partial failure still refreshes the library.
  const deleteMutation = useMutation({
    mutationFn: (ids) => Promise.allSettled(ids.map((id) => prepacksApi.deletePrepack(slug, id))),
    onSettled: invalidate,
  })

  // The confirmation is built from fresh details: a selected or open pack may
  // have been published, placed or deleted by someone else since it loaded.
  // A bulk request belongs to the selection it started from; if the operator
  // clears or changes the selection while it loads, the result is dropped.
  const requestDelete = async (ids, { fromSelection = false } = {}) => {
    deleteOpener.current = document.activeElement
    const requestSlug = slug
    const requestSelection = fromSelection ? selectedPacks : null
    setPreparingDelete(true)
    let results
    try {
      results = await Promise.allSettled(ids.map((id) => prepacksApi.getPrepack(requestSlug, id)))
    } finally {
      setPreparingDelete(false)
    }
    if (slugRef.current !== requestSlug) return
    if (fromSelection && selectedPacksRef.current !== requestSelection) return
    const goneIds = new Set(ids.filter((_, index) => results[index].status === 'rejected' && results[index].reason?.response?.status === 404))
    if (results.some((result, index) => result.status === 'rejected' && !goneIds.has(ids[index]))) {
      toast.error('Could not load the latest prepack details. Try again.')
      return
    }
    const fresh = results.filter((result) => result.status === 'fulfilled').map((result) => normalizePrepack(result.value))
    setSelectedPacks((current) => {
      const next = new Map(current)
      goneIds.forEach((id) => next.delete(id))
      fresh.forEach((pack) => { if (next.has(pack.id)) next.set(pack.id, pack) })
      return next
    })
    if (goneIds.size > 0) invalidate()
    const packs = fresh.filter(canDelete)
    if (packs.length > 0) {
      setPendingDelete({ packs, skipped: fresh.length - packs.length })
    } else if (fresh.length > 0) {
      toast.error('Deleting published prepacks requires publish permission.')
    } else {
      toast.error(ids.length === 1 ? 'This prepack was already deleted.' : 'The selected prepacks were already deleted.')
    }
  }

  const deletePacks = async () => {
    if (!pendingDelete) return
    const { packs } = pendingDelete
    const results = await deleteMutation.mutateAsync(packs.map((pack) => pack.id))
    const deletedIds = new Set(packs.filter((_, index) => results[index].status === 'fulfilled').map((pack) => pack.id))
    const failures = results.filter((result) => result.status === 'rejected')
    setPendingDelete(null)
    // Failed packs stay selected so the operator can retry them.
    setSelectedPacks((current) => {
      const next = new Map(current)
      deletedIds.forEach((id) => next.delete(id))
      return next
    })
    setOpenPack((current) => (current && deletedIds.has(current.id) ? null : current))
    if (failures.length === 0) {
      toast.success(packs.length === 1 ? 'Prepack deleted' : `${packs.length} prepacks deleted`)
      return
    }
    const reason = failures[0].reason?.response?.data?.error
    if (packs.length === 1) {
      toast.error(reason || 'Failed to delete prepack')
    } else {
      toast.error(`Deleted ${deletedIds.size} of ${packs.length} prepacks. ${reason || 'Some deletes failed.'}`)
    }
  }

  const count = pendingDelete?.packs.length || 0
  return {
    requestDelete,
    preparingDelete,
    confirmProps: {
      open: Boolean(pendingDelete),
      title: count > 1 ? `Delete ${count} prepacks?` : 'Delete prepack?',
      message: pendingDelete ? deleteConfirmMessage(pendingDelete) : '',
      variant: 'danger',
      confirmLabel: count > 1 ? `Delete ${count} prepacks` : 'Delete prepack',
      loading: deleteMutation.isPending,
      onConfirm: deletePacks,
      onCancel: () => { if (!deleteMutation.isPending) setPendingDelete(null) },
      returnFocusRef: deleteOpener,
    },
  }
}
